import React, { useState, useEffect } from 'react';
import { db, auth } from './firebase';
import { collection, query, where, getDocs, doc, serverTimestamp, increment, onSnapshot, orderBy, runTransaction } from 'firebase/firestore';
import * as XLSX from 'xlsx';

const Returns = () => {
  const [invNo, setInvNo] = useState('');
  const [saleData, setSaleData] = useState(null);
  const [, setLoading] = useState(false);
  const [returnQty, setReturnQty] = useState({});
  const [returnReason, setReturnReason] = useState('');
  const [deliveryChargeDeduction, setDeliveryChargeDeduction] = useState('');
  const [returnHistory, setReturnHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  
  const [previewReturn, setPreviewReturn] = useState(null);
  const [viewOnly, setViewOnly] = useState(false);

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const formatTk = (num) => new Intl.NumberFormat('en-IN').format(num || 0);

  useEffect(() => {
    const q = query(collection(db, "returns"), orderBy("createdAt", "desc"));
    return onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setReturnHistory(data);
      setFilteredHistory(data);
    });
  }, []);

  const searchInvoice = async () => {
    if (!invNo) return;
    setLoading(true);
    const cleanInv = invNo.replace('#', '').trim();

    try {
      const returnCheckQuery = query(collection(db, "returns"), where("originalInvId", "==", cleanInv));
      const returnCheckSnap = await getDocs(returnCheckQuery);
      
      if (!returnCheckSnap.empty) {
        alert("❌ This invoice has already been returned once! It cannot be returned again.");
        setLoading(false);
        setSaleData(null);
        return;
      }

      const q = query(collection(db, "sales"), where("invoiceId", "==", cleanInv));
      const snap = await getDocs(q);
      if (!snap.empty) {
        setSaleData({ id: snap.docs[0].id, ...snap.docs[0].data() });
        setReturnQty({});
        setReturnReason('');
        setDeliveryChargeDeduction('');
      } else {
        alert("❌ Invoice not found!");
        setSaleData(null);
      }
    } catch (err) {
      alert("Error: " + err.message);
    }
    setLoading(false);
  };

  const handleCancelReturn = () => {
    setSaleData(null);
    setInvNo('');
    setReturnQty({});
    setReturnReason('');
    setDeliveryChargeDeduction('');
  };

  // ---- Discount হিসাব একটা জায়গায় বের করে রাখলাম, যাতে UI-তে ও রিফান্ড ক্যালকুলেশনে একই ভ্যালু ব্যবহার হয় ----
  const getDiscountInfo = () => {
    if (!saleData || !saleData.items) {
      return { subTotalOriginal: 0, actualTotalBill: 0, discountAmount: 0, discountRatio: 1, hasDiscount: false };
    }
    const subTotalOriginal = saleData.items.reduce((acc, item) => acc + (item.price * item.qty), 0);
    const actualTotalBill = saleData.totalBill !== undefined ? saleData.totalBill : subTotalOriginal;
    const hasDiscount = subTotalOriginal > 0 && actualTotalBill < subTotalOriginal;
    const discountAmount = hasDiscount ? (subTotalOriginal - actualTotalBill) : 0;
    const discountRatio = hasDiscount ? (actualTotalBill / subTotalOriginal) : 1;
    return { subTotalOriginal, actualTotalBill, discountAmount, discountRatio, hasDiscount };
  };

  const calculateTotalRefund = () => {
    if (!saleData || !saleData.items) return 0;

    const { discountRatio } = getDiscountInfo();

    let currentItemOriginalTotal = saleData.items.reduce((total, item) => {
      const qty = Number(returnQty[item.id] || 0);
      return total + (item.price * qty);
    }, 0);

    currentItemOriginalTotal = currentItemOriginalTotal * discountRatio;

    const deduction = Math.max(0, Number(deliveryChargeDeduction) || 0);
    const finalRefund = currentItemOriginalTotal - deduction;

    return Math.max(0, Math.round(finalRefund));
  };

  const totalRefundAmount = calculateTotalRefund();
  const discountInfo = getDiscountInfo();

  const handleFilter = () => {
    if (!startDate || !endDate) return alert("Please enter dates!");
    const filtered = returnHistory.filter(item => {
      const d = item.createdAt?.toDate();
      const itemDate = d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : '';
      return itemDate >= startDate && itemDate <= endDate;
    });
    setFilteredHistory(filtered);
  };

  const handleViewInvoice = (log) => {
    setPreviewReturn(log);
    setViewOnly(true);
  };

  const showReturnPreview = () => {
    if (!saleData || !saleData.items) return;

    const { discountRatio } = getDiscountInfo();

    const itemsToReturn = saleData.items
      .filter(item => Number(returnQty[item.id]) > 0)
      .map(item => {
        const rQty = Number(returnQty[item.id]);
        const itemOrigTotal = item.price * rQty;
        const itemRefundTotal = Math.round(itemOrigTotal * discountRatio);
        return { 
          ...item, 
          returnQty: rQty, 
          returnTotal: itemRefundTotal 
        };
      });

    if (itemsToReturn.length === 0) return alert("Please enter a return quantity!");

    const deductionVal = Math.max(0, Number(deliveryChargeDeduction) || 0);

    setPreviewReturn({
      originalInvId: saleData.invoiceId,
      originalDate: saleData.dateTime || saleData.date || "N/A",
      originalTotalBill: saleData.totalBill || 0,
      originalDiscount: discountInfo.discountAmount || saleData.discount || 0,
      originalPaid: saleData.paid || 0,
      // Date.now() (base36) -- Sales.js-এর invoiceId-এর মতোই -- যাতে দুইটা আলাদা রিটার্ন
      // কখনো একই রেফারেন্স নম্বর শেয়ার না করে।
      returnId: "RET-" + Date.now().toString(36).toUpperCase(),
      customerName: saleData.name || "Walk-In Customer",
      processedBy: auth.currentUser?.displayName || auth.currentUser?.email?.split('@')[0] || 'Staff', // রিটার্ন গ্রহণকারী ইউজারের নাম
      items: itemsToReturn,
      deliveryChargeDeduction: deductionVal,
      totalRefund: totalRefundAmount,
      reason: returnReason || "Not Specified",
      date: new Date().toLocaleDateString('en-GB'),
      time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })
    });
    setViewOnly(false);
  };

  const handleFinalReturn = async (shouldPrint) => {
    setLoading(true);
    try {
      // ---- আগে রিটার্ন করলে শুধু স্টক ফেরত যেত আর returns কালেকশনে একটা রেকর্ড লেখা হতো --
      // আসল sales ডকুমেন্টের totalBill/dueAmount কখনো আপডেট হতো না, আর productStats-এর
      // qtySold কাউন্টারও কমতো না। ফলাফল: রিটার্ন হওয়া আইটেমও Accounts & Ledger-এর Net
      // Profit/Total Income-এ পুরোপুরি ধরা পড়তো (returns কালেকশন Accounts.js কখনো পড়েই না),
      // আর কাস্টমার টাকা না দিয়ে থাকলে রিটার্নের পরও Due পেজে সেই পুরনো বকেয়াই দেখাতে
      // থাকতো, আর Home ড্যাশবোর্ডের "Top Selling Products"ও কখনো কমতো না।
      //
      // এখন পুরো অপারেশনটা একটা transaction-এ atomic ভাবে হচ্ছে: স্টক ফেরত, productStats
      // কমানো, sales ডকুমেন্টের totalBill/dueAmount রিটার্ন হওয়া অংশ দিয়ে কমানো (discount
      // অনুপাত ধরেই, যেটা previewReturn.items[].returnTotal-এ আগে থেকেই হিসাব করা আছে),
      // আর returns রেকর্ড লেখা -- সবকিছু একসাথে সফল হবে, নাহলে কিছুই হবে না।
      //
      // "Paid" ফিল্ড ইচ্ছাকৃতভাবে ছোঁয়া হচ্ছে না -- ওটা ঐতিহাসিকভাবে সত্যিই কত টাকা হাতে
      // পাওয়া হয়েছিল সেটাই দেখাবে (App-এর Advance/Paid টগলের ক্ষেত্রেও একই নীতি মানা হয়)।
      // যদি রিটার্ন হওয়া অংশ বাকি টাকার (due) চেয়ে বেশি হয়, তার মানে কাস্টমার সেই অংশ
      // আগেই দিয়ে দিয়েছিল -- সেই ক্ষেত্রে নগদ ফেরত দেওয়ার হিসাব totalRefundAmount-এই আছে,
      // dueAmount শুধু 0-এ গিয়ে থামবে (নেগেটিভ হবে না)।
      const returnedBillPortion = previewReturn.items.reduce((sum, it) => sum + (Number(it.returnTotal) || 0), 0);

      await runTransaction(db, async (transaction) => {
        // ---- Firestore transaction-এ সব read আগে করতে হয়, তারপর write ----
        const saleRef = saleData?.id ? doc(db, "sales", saleData.id) : null;
        const saleSnap = saleRef ? await transaction.get(saleRef) : null;

        previewReturn.items.forEach(item => {
          transaction.update(doc(db, "products", item.id), { stock: increment(item.returnQty) });
          transaction.set(doc(db, "productStats", item.id), { qtySold: increment(-item.returnQty) }, { merge: true });
        });

        if (saleSnap && saleSnap.exists()) {
          const saleNow = saleSnap.data();
          const currentDue = Number(saleNow.dueAmount || 0);
          const currentTotalBill = Number(saleNow.totalBill || 0);
          transaction.update(saleRef, {
            totalBill: Math.max(0, currentTotalBill - returnedBillPortion),
            dueAmount: Math.max(0, currentDue - returnedBillPortion)
          });
        }

        transaction.set(doc(collection(db, "returns")), { ...previewReturn, createdAt: serverTimestamp() });
      });

      if (shouldPrint) window.print();
      alert("✅ Return successful!");
      handleCancelReturn();
      setPreviewReturn(null);
    } catch (err) { alert(err.message); } finally { setLoading(false); }
  };

  const exportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filteredHistory.map(r => ({ Date: r.date, ID: r.returnId, OrigInv: r.originalInvId, Cust: r.customerName, Reason: r.reason, Refund: r.totalRefund })));
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, "Returns");
    XLSX.writeFile(wb, "Returns_Report.xlsx");
  };

  return (
    <div style={{ padding: '20px' }}>
      
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #printable-invoice, #printable-invoice * {
            visibility: visible;
          }
          #printable-invoice {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            margin: 0;
            padding: 10px;
            background: #fff !important;
          }
          .no-print {
            display: none !important;
          }
        }
      `}</style>

      {/* এন্ট্রি কার্ড: পুরো কার্ড viewport height-এর মধ্যে ফিট থাকে (maxHeight + flex column),
          শুধু মাঝের ডিটেইলস অংশ প্রয়োজনে internal scroll করবে — উপরের সার্চবার আর নিচের
          Cancel/Review বাটন সবসময় দেখা যাবে, ডিজাইন অপরিবর্তিত। */}
      <div style={{ maxWidth: '600px', margin: '0 auto 40px', height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' }} className="no-print">
        <div
          className="white-box"
          style={{
            padding: '30px 40px',
            borderRadius: '25px',
            textAlign: 'center',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden'
          }}
        >
          
          {saleData && (
            <button 
              onClick={handleCancelReturn} 
              style={{ position: 'absolute', top: '20px', right: '20px', background: '#ffeded', color: 'red', border: 'none', width: '30px', height: '30px', borderRadius: '50%', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' }}
              title="Cancel / Close"
            >
              ✕
            </button>
          )}

          <div style={{ flexShrink: 0 }}>
            <h2 style={{ color: '#4a00e0', marginBottom: '16px' }}>Product Return Entry</h2>
            <div style={{ display: 'flex', gap: '10px' }}>
              <input className="input-field" placeholder="Invoice ID (INV-XXXXXX)" value={invNo} onChange={e => setInvNo(e.target.value)} style={{ textAlign: 'center', marginBottom: 0 }}/>
              <button onClick={searchInvoice} className="btn-confirm" style={{ width: '120px', padding: '12px' }}>Find Sale</button>
            </div>
          </div>
          
          {saleData && (
            <>
              {/* --- স্ক্রলযোগ্য মাঝের অংশ: কাস্টমার ইনফো + আইটেম লিস্ট + ডেলিভারি চার্জ + রিজন --- */}
              <div style={{ textAlign: 'left', background: '#f8f9ff', padding: '20px', borderRadius: '20px', marginTop: '16px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
                <p style={{ margin: '0 0 4px 0' }}><b>Customer:</b> {saleData.name || "Walk-In"}</p>
                {/* --- মূল বিক্রির তারিখ এখন স্পষ্টভাবে দেখানো হচ্ছে --- */}
                <p style={{ margin: '0 0 4px 0', fontSize: '13px', color: '#444' }}>
                  <b>Sale Date:</b> {saleData.dateTime || saleData.date || "N/A"}
                </p>
                <p style={{fontSize: '12px', color: '#666', margin: '0 0 4px 0'}}>
                  Original Invoice Total: ৳ {formatTk(saleData.totalBill)}
                </p>
                {/* --- ডিসকাউন্টের পরিমাণ ও শতকরা হার স্পষ্টভাবে দেখানো হচ্ছে --- */}
                {discountInfo.hasDiscount && (
                  <p style={{ fontSize: '12px', color: '#d63031', fontWeight: 'bold', margin: '0 0 4px 0', background: '#fff0f0', padding: '6px 10px', borderRadius: '8px' }}>
                    ⚠️ A discount was given on this sale: ৳ {formatTk(discountInfo.discountAmount)}
                    {' '}({Math.round((1 - discountInfo.discountRatio) * 100)}% off)
                    — so the refund will be calculated at the same reduced rate.
                  </p>
                )}
                <div style={{borderBottom:'1px solid #ddd', margin:'10px 0'}}></div>
                
                {saleData.items.map(item => {
                  const discountedUnitPrice = Math.round(item.price * discountInfo.discountRatio);
                  return (
                    <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px' }}>
                      <span>
                        <b>{item.name}</b> <br/>
                        <small>Sold: {item.qty} pcs | Unit Price: ৳ {formatTk(item.price)}</small>
                        {discountInfo.hasDiscount && (
                          <>
                            <br/>
                            <small style={{ color: '#d63031' }}>
                              Actual refund rate (after discount): ৳ {formatTk(discountedUnitPrice)}/pcs
                            </small>
                          </>
                        )}
                      </span>
                      <div style={{ textAlign: 'center' }}>
                        {/* --- ইনপুট বক্সের উপরে লেবেল যোগ করা হলো --- */}
                        <label style={{ fontSize: '10px', color: '#888', fontWeight: 'bold', display: 'block', marginBottom: '3px', letterSpacing: '0.5px' }}>
                          RETURN QTY
                        </label>
                        <input 
                          type="number" max={item.qty} min="0" placeholder="0" 
                          style={{ width: '70px', padding:'8px', borderRadius:'8px', border:'1px solid #ccc', textAlign: 'center' }} 
                          value={returnQty[item.id] || ''}
                          onChange={e => setReturnQty({ ...returnQty, [item.id]: e.target.value })}
                        />
                      </div>
                    </div>
                  );
                })}

                <div style={{ marginTop: '15px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#555' }}>Deduct Delivery / Other Charge (৳):</label>
                  <input 
                    type="number" 
                    min="0"
                    placeholder="0" 
                    value={deliveryChargeDeduction} 
                    onChange={e => setDeliveryChargeDeduction(e.target.value)}
                    className="input-field"
                    style={{ marginTop: '5px', marginBottom: 0, padding: '10px' }}
                  />
                </div>

                <div style={{ marginTop: '15px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#555' }}>Return Reason:</label>
                  <input 
                    type="text" 
                    placeholder="e.g. size too small, defective, etc..."
                    value={returnReason} 
                    onChange={e => setReturnReason(e.target.value)}
                    className="input-field"
                    style={{ marginTop: '5px', marginBottom: 0, padding: '10px' }}
                  />
                </div>
              </div>

              {/* --- নিচের অংশ (Refund সামারি + বাটন) সবসময় স্ক্রিনে স্থির থাকবে, স্ক্রল হবে না --- */}
              <div style={{ flexShrink: 0 }}>
                <div style={{ textAlign: 'right', borderTop: '2px dashed #ccc', marginTop: '12px', paddingTop: '10px' }}>
                  {discountInfo.hasDiscount && (
                    <p style={{ fontSize: '12px', color: '#666', margin: '0 0 4px 0' }}>
                      Discount Adjustment Applied ({Math.round((1 - discountInfo.discountRatio) * 100)}%)
                    </p>
                  )}
                  {deliveryChargeDeduction > 0 && (
                    <p style={{ fontSize: '12px', color: '#666', margin: '0 0 4px 0' }}>
                      Deduction: -৳ {formatTk(deliveryChargeDeduction)}
                    </p>
                  )}
                  <h3 style={{ color: 'red', margin: 0 }}>Refund: ৳ {formatTk(totalRefundAmount)}</h3>
                </div>

                <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
                  <button onClick={handleCancelReturn} style={{ flex: 1, padding: '12px', borderRadius: '12px', border: '1px solid #ccc', background: '#fff', cursor: 'pointer', fontWeight: 'bold' }}>Cancel</button>
                  <button onClick={showReturnPreview} className="btn-confirm" style={{ flex: 2, background: '#ff4d4d', margin: 0 }}>REVIEW & CONFIRM</button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* রিপোর্ট টেবিল */}
      <div className="white-box no-print" style={{ padding: '30px', borderRadius: '25px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap:'wrap', gap:'10px' }}>
          <h3>Return History Reports</h3>
          <div style={{ display: 'flex', gap: '10px' }}>
            <input type="date" className="input-field" value={startDate} onChange={e=>setStartDate(e.target.value)} style={{width:'150px', marginBottom:0}}/>
            <input type="date" className="input-field" value={endDate} onChange={e=>setEndDate(e.target.value)} style={{width:'150px', marginBottom:0}}/>
            <button onClick={handleFilter} className="btn-confirm" style={{width:'80px', padding:'10px'}}>VIEW</button>
            <button onClick={exportExcel} style={{background:'#27ae60', color:'#fff', border:'none', padding:'0 15px', borderRadius:'10px', cursor:'pointer', fontWeight:'bold'}}>EXCEL</button>
          </div>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ borderBottom: '2px solid #f4f7fe', color: '#888', textAlign: 'left' }}>
            <th style={{padding:'15px'}}>DATE</th><th>RETURN ID</th><th>CUSTOMER</th><th>REASON</th><th>AMOUNT</th>
          </tr></thead>
          <tbody>
            {filteredHistory.map(log => (
              <tr key={log.id} style={{ borderBottom: '1px solid #f9f9f9' }}>
                <td style={{padding:'15px'}}>{log.date}</td>
                <td><b onClick={() => handleViewInvoice(log)} style={{ color: '#4a00e0', cursor: 'pointer', textDecoration: 'underline' }}>#{log.returnId || log.invoiceId}</b></td>
                <td>{log.customerName}</td>
                <td>{log.reason || 'N/A'}</td>
                <td style={{ color: 'red', fontWeight: 'bold' }}>৳ {formatTk(log.totalRefund)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* স্প্লিট-ভিউ মেমো পপ-আপ */}
      {previewReturn && (
        <div className="modal-overlay">
          <div style={{ background:'#fff', padding:'40px', borderRadius:'25px', width:'500px', maxHeight: '90vh', overflowY: 'auto', position:'relative' }}>
            <button onClick={()=>setPreviewReturn(null)} className="no-print" style={{ position:'absolute', top:'15px', right:'15px', border:'none', background:'#ffeded', color:'red', width:'30px', height:'30px', borderRadius:'50%', cursor:'pointer', fontWeight:'bold' }}>X</button>
            
            <div id="printable-invoice" style={{ background: '#fff', padding: '10px' }}>
              <div style={{ textAlign:'center', marginBottom:'15px' }}>
                <h2 style={{ color:'#4a00e0', margin:0 }}>KIZUNA COMPANY LTD</h2>
                <p style={{ margin:'5px 0', fontSize:'11px', fontWeight:'bold' }}>H-10, R-2, Section-2, Block-D, Aftabnagar, Dhaka</p>
                <div style={{ borderBottom:'1px dashed #000', margin:'10px 0' }}></div>
                <p style={{fontSize:'12px', fontWeight:'bold', margin:0}}>SALES RETURN VOUCHER</p>
              </div>
              
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:'11px', background:'#f9f9f9', padding:'10px', borderRadius:'10px', marginBottom:'15px' }}>
                <div style={{flex:1}}>
                  <p style={{color:'#888', fontWeight:'bold', marginBottom:'3px'}}>ORIGINAL SALE</p>
                  <p>Inv: #{previewReturn.originalInvId}</p>
                  <p>Date: {previewReturn.originalDate}</p>
                  <p>Cust: {previewReturn.customerName}</p>
                  <p>Orig. Bill: ৳ {formatTk(previewReturn.originalTotalBill)}</p>
                  {previewReturn.originalDiscount > 0 && (
                    <p style={{ color: '#d63031' }}>Discount: ৳ {formatTk(previewReturn.originalDiscount)}</p>
                  )}
                </div>
                <div style={{ textAlign:'right', borderLeft:'1px dashed #ccc', paddingLeft:'10px', flex:1 }}>
                  <p style={{color:'#888', fontWeight:'bold', marginBottom:'3px'}}>RETURN INFO</p>
                  <p>ID: #{previewReturn.returnId}</p>
                  <p>Date: {previewReturn.date}</p>
                  <p>Time: {previewReturn.time}</p>
                  <p>By: {previewReturn.processedBy || "Admin"}</p>
                </div>
              </div>

              <div style={{ fontSize: '12px', marginBottom: '10px' }}>
                <p><b>Return Reason:</b> <span style={{color: '#d63031'}}>{previewReturn.reason}</span></p>
              </div>

              <table style={{ width:'100%', fontSize:'12px', borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{borderBottom:'1px solid #eee', color:'#888'}}>
                    <th align="left" style={{padding:'5px 0'}}>ITEM</th>
                    <th align="center">QTY</th>
                    <th align="right">REFUND</th>
                  </tr>
                </thead>
                <tbody>
                  {previewReturn.items.map((it, i) => (
                    <tr key={i} style={{borderBottom:'1px solid #f9f9f9'}}>
                      <td style={{padding:'6px 0'}}>{it.name}</td>
                      <td align="center">{it.returnQty}</td>
                      <td align="right">{formatTk(it.returnTotal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div style={{ borderTop:'1px solid #eee', marginTop:'15px', paddingTop:'10px', textAlign:'right', fontSize: '12px' }}>
                {previewReturn.deliveryChargeDeduction > 0 && (
                  <p style={{ margin: '3px 0', color: '#666' }}>Delivery/Other Deduction: -৳ {formatTk(previewReturn.deliveryChargeDeduction)}</p>
                )}
                <h3 style={{ color:'red', margin: '5px 0' }}>Total Refund: ৳ {formatTk(previewReturn.totalRefund)}</h3>
              </div>
              
              <p style={{ textAlign:'center', marginTop:'20px', fontSize:'10px' }}>Software by Kizuna Tech</p>
            </div>

            <div className="no-print" style={{ display:'flex', gap:'10px', marginTop:'20px' }}>
              {!viewOnly ? (
                <>
                  <button onClick={()=>handleFinalReturn(false)} style={{ flex:1, padding:'12px', borderRadius:'12px', border:'1px solid #ddd', cursor:'pointer', fontWeight:'bold' }}>Save Only</button>
                  <button onClick={()=>handleFinalReturn(true)} className="btn-confirm" style={{ flex:2, background:'#4a00e0' }}>Print Return Memo 🖨️</button>
                </>
              ) : (
                <button onClick={()=>window.print()} className="btn-confirm" style={{ width:'100%', background:'#4a00e0' }}>PRINT VOUCHER 🖨️</button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Returns;